/**
 * DOM-based UI highlight system for the ads companion.
 * Uses data-highlight-id attributes + CSS box-shadow pulse animation.
 * No React wrappers or Zustand stores — pure imperative DOM manipulation.
 */

interface HighlightOptions {
  /** The data-highlight-id value to target */
  target: string;
  /** Optional tooltip text shown above the element */
  comment?: string;
  /** Number of pulse cycles (default 3) */
  pulses?: number;
}

const RETRY_INTERVAL_MS = 200;
const MAX_RETRIES = 15;
const ANIMATION_DURATION_MS = 800; // matches CSS animation duration
const SAFETY_BUFFER_MS = 500;
const TOOLTIP_CLASS = "ui-highlight-tooltip";
const TOOLTIP_MARGIN_PX = 8;
const MIDDLE_OUTSIDE_TOOLTIP_PLACEMENT = "outside-middle";
let tooltipCleanup: (() => void) | null = null;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/** Remove any existing highlight tooltip from the DOM */
function removeTooltip() {
  const cleanup = tooltipCleanup;
  tooltipCleanup = null;
  cleanup?.();
  document.querySelectorAll(`.${TOOLTIP_CLASS}`).forEach((el) => el.remove());
}

/** Remove highlight class from all elements */
function clearHighlights() {
  document.querySelectorAll(".ui-highlight-pulse").forEach((el) => {
    el.classList.remove("ui-highlight-pulse");
    (el as HTMLElement).style.animationIterationCount = "";
  });
  removeTooltip();
}

function positionTooltip(anchor: HTMLElement, tip: HTMLElement) {
  const rect = anchor.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  const availableWidth = Math.max(0, window.innerWidth - TOOLTIP_MARGIN_PX * 2);
  const tooltipWidth = tipRect.width > 0 ? Math.min(tipRect.width, availableWidth) : tipRect.width;
  const tooltipHeight = tipRect.height;
  const maxTop = window.innerHeight - tooltipHeight - TOOLTIP_MARGIN_PX;

  if (anchor.dataset.highlightTooltipPlacement === MIDDLE_OUTSIDE_TOOLTIP_PLACEMENT) {
    const leftSpace = rect.left - TOOLTIP_MARGIN_PX;
    const rightSpace = window.innerWidth - rect.right - TOOLTIP_MARGIN_PX;
    const leftFits = leftSpace >= tooltipWidth;
    const rightFits = rightSpace >= tooltipWidth;
    const useLeftSide = leftFits || (!rightFits && leftSpace >= rightSpace);
    const preferredLeft = useLeftSide
      ? rect.left - tooltipWidth - TOOLTIP_MARGIN_PX
      : rect.right + TOOLTIP_MARGIN_PX;

    tip.style.top = `${clamp(
      rect.top + rect.height / 2 - tooltipHeight / 2,
      TOOLTIP_MARGIN_PX,
      maxTop,
    )}px`;
    tip.style.left = `${clamp(
      preferredLeft,
      TOOLTIP_MARGIN_PX,
      window.innerWidth - tooltipWidth - TOOLTIP_MARGIN_PX,
    )}px`;
    return;
  }

  const preferredTop = rect.top - tooltipHeight - TOOLTIP_MARGIN_PX;
  const fallbackTop = rect.bottom + TOOLTIP_MARGIN_PX;
  const top = clamp(
    preferredTop >= TOOLTIP_MARGIN_PX ? preferredTop : fallbackTop,
    TOOLTIP_MARGIN_PX,
    maxTop,
  );
  const left = clamp(
    rect.left + rect.width / 2 - tooltipWidth / 2,
    TOOLTIP_MARGIN_PX,
    window.innerWidth - tooltipWidth - TOOLTIP_MARGIN_PX,
  );

  tip.style.top = `${top}px`;
  tip.style.left = `${left}px`;
}

function createTooltip(anchor: HTMLElement, text: string) {
  removeTooltip();

  const tip = document.createElement("div");
  tip.className = TOOLTIP_CLASS;
  tip.textContent = text;

  Object.assign(tip.style, {
    position: "fixed",
    zIndex: "9999",
    boxSizing: "border-box",
    maxWidth: "min(300px, calc(100vw - 16px))",
    padding: "10px 14px",
    borderRadius: "16px",
    border: "1px solid rgba(231, 231, 228, 0.72)",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "1.45",
    color: "#f4f0ef",
    background: "rgba(49, 48, 48, 0.94)",
    backdropFilter: "blur(20px)",
    boxShadow: "0 18px 52px rgba(0,0,0,0.16), 0 0 0 1px rgba(255,255,255,0.06)",
    pointerEvents: "none",
    overflowWrap: "break-word",
    opacity: "0",
    transition: "opacity 0.2s ease",
  });

  document.body.appendChild(tip);

  let raf = 0;
  let active = true;
  const updatePosition = () => {
    if (!active) return;
    if (!document.body.contains(tip) || !document.body.contains(anchor)) {
      active = false;
      return;
    }
    positionTooltip(anchor, tip);
  };
  const tick = () => {
    updatePosition();
    if (active) raf = requestAnimationFrame(tick);
  };

  updatePosition();
  raf = requestAnimationFrame(tick);
  window.addEventListener("resize", updatePosition);
  window.addEventListener("scroll", updatePosition, true);
  tooltipCleanup = () => {
    active = false;
    cancelAnimationFrame(raf);
    window.removeEventListener("resize", updatePosition);
    window.removeEventListener("scroll", updatePosition, true);
  };

  // Fade in
  requestAnimationFrame(() => {
    tip.style.opacity = "1";
  });

  return tip;
}

/**
 * Highlight a UI element by its data-highlight-id.
 * Retries if the element is not yet in the DOM (e.g., tab switching).
 */
export function highlightElement(options: HighlightOptions): void {
  const { target, comment, pulses = 3 } = options;
  let retries = 0;

  clearHighlights();

  const tryHighlight = () => {
    const el = document.querySelector<HTMLElement>(`[data-highlight-id="${target}"]`);

    if (!el) {
      retries++;
      if (retries < MAX_RETRIES) {
        setTimeout(tryHighlight, RETRY_INTERVAL_MS);
      }
      return;
    }

    // Scroll into view smoothly
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });

    // Apply pulse animation
    el.classList.add("ui-highlight-pulse");
    el.style.animationIterationCount = String(pulses);

    // Optional tooltip
    if (comment) {
      // Small delay so scroll completes before positioning
      setTimeout(() => createTooltip(el, comment), 300);
    }

    // Cleanup after animation completes
    const cleanup = () => {
      el.classList.remove("ui-highlight-pulse");
      el.style.animationIterationCount = "";
      removeTooltip();
    };

    el.addEventListener("animationend", cleanup, { once: true });

    // Safety timeout in case animationend doesn't fire
    setTimeout(cleanup, pulses * ANIMATION_DURATION_MS + SAFETY_BUFFER_MS);
  };

  tryHighlight();
}
